import { create } from 'zustand';
import type { NotificationItem } from '../../../shared/types/index.js';
import { safeLocalStorage } from '../../../shared/utils/safeLocalStorage.js';

interface NotificationState {
  notifications: NotificationItem[];
  unreadCount: number;
  pushEnabled: boolean;
  isOpen: boolean;
  toast: { title: string; message: string; type: string } | null;
  toggleOpen: (open?: boolean) => void;
  setPushEnabled: (enabled: boolean) => void;
  fetchNotifications: (token: string | null) => Promise<void>;
  markAsRead: (id: string, token: string | null) => Promise<void>;
  markAllRead: (token: string | null) => Promise<void>;
  triggerTestPush: (token: string | null) => Promise<void>;
  showToast: (title: string, message: string, type?: string) => void;
  clearToast: () => void;
  clear: () => void;
  deleteNotification: (id: string, token: string | null) => Promise<void>;
  deleteReadNotifications: (token: string | null) => Promise<void>;
}

// Gentle audio chime for push notification
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
    osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.15); // A5
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.36);
  } catch {
    // audio context might be blocked without interaction
  }
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  pushEnabled: safeLocalStorage.getItem('uibn_push_enabled') === 'true',
  isOpen: false,
  toast: null,

  toggleOpen: (open?: boolean) => {
    set((state) => ({ isOpen: open !== undefined ? open : !state.isOpen }));
  },

  setPushEnabled: (enabled: boolean) => {
    safeLocalStorage.setItem('uibn_push_enabled', String(enabled));
    set({ pushEnabled: enabled });
    if (enabled && 'Notification' in window && Notification.permission !== 'granted') {
      try {
        Notification.requestPermission();
      } catch {
        // ignore in restrictive frames
      }
    }
  },

  showToast: (title: string, message: string, type = 'info') => {
    playNotificationSound();
    set({ toast: { title, message, type } });
    setTimeout(() => {
      if (get().toast?.title === title) {
        set({ toast: null });
      }
    }, 4500);
  },

  clearToast: () => set({ toast: null }),

  clear: () => set({ notifications: [], unreadCount: 0 }),

  fetchNotifications: async (token: string | null) => {
    if (!token) {
      set({ notifications: [], unreadCount: 0 });
      return;
    }
    try {
      const res = await fetch('/api/notifications', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const prevUnread = get().unreadCount;
        set({
          notifications: data.notifications || [],
          unreadCount: data.unreadCount || 0,
        });

        // If new notifications arrived while user was on page
        if (data.unreadCount > prevUnread && data.notifications.length > 0) {
          const newest = data.notifications[0];
          get().showToast(newest.title, newest.message, 'success');
        }
      }
    } catch {
      // offline fallback
    }
  },

  markAsRead: async (id: string, token: string | null) => {
    if (!token) return;
    try {
      await fetch(`/api/notifications/${id}/read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      set((state) => {
        const updated = state.notifications.map((n) => (n.id === id ? { ...n, isRead: 1 } : n));
        return {
          notifications: updated,
          unreadCount: Math.max(0, updated.filter((n) => n.isRead === 0).length),
        };
      });
    } catch {
      // error handling
    }
  },

  markAllRead: async (token: string | null) => {
    if (!token) return;
    try {
      await fetch('/api/notifications/read-all', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      set((state) => ({
        notifications: state.notifications.map((n) => ({ ...n, isRead: 1 })),
        unreadCount: 0,
      }));
    } catch {
      // error handling
    }
  },

  triggerTestPush: async (token: string | null) => {
    if (!token) return;
    try {
      const res = await fetch('/api/notifications/test-push', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        get().showToast(data.notification.title, data.notification.message, 'success');
        await get().fetchNotifications(token);
      }
    } catch {
      // error handling
    }
  },

  deleteNotification: async (id: string, token: string | null) => {
    if (!token) return;
    try {
      await fetch(`/api/notifications/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      set((state) => {
        const updated = state.notifications.filter((n) => n.id !== id);
        return {
          notifications: updated,
          unreadCount: Math.max(0, updated.filter((n) => n.isRead === 0).length),
        };
      });
    } catch {
      // error handling
    }
  },

  deleteReadNotifications: async (token: string | null) => {
    if (!token) return;
    try {
      await fetch('/api/notifications/delete-read', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      set((state) => ({
        notifications: state.notifications.filter((n) => n.isRead === 0),
        unreadCount: Math.max(0, state.notifications.filter((n) => n.isRead === 0).length),
      }));
    } catch {
      // error handling
    }
  },
}));
